<template>
  <b-modal
    visible
    title="Supplier Ledger Settings"
    size="xl"
    scrollable
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
  >
    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$emit('modal-closed')"
      >
        Close
      </b-button>
    </template>

    <!-- Add / Edit Form -->
    <b-card class="mb-1 bg-light">
      <h6 class="mb-1">
        {{ editingItem ? 'Edit Setting' : 'Add New Setting' }}
      </h6>
      <b-row class="d-flex align-items-end">
        <b-col md="4">
          <b-form-group
            label="Supplier"
            label-class="small"
          >
            <custom-v-select
              v-model="form.supplier"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="supplierOptions"
              :loading="supplierOptionsLoading"
              label="text"
              :reduce="opt => opt.id"
              placeholder="Select Supplier"
              :disabled="!!editingItem"
            />
          </b-form-group>
        </b-col>
        <b-col md="1">
          <b-form-group
            label="Start Row"
            label-class="small"
          >
            <b-form-input
              v-model="form.start_row"
              type="number"
              min="1"
              size="sm"
              placeholder="e.g. 2"
            />
          </b-form-group>
        </b-col>
        <b-col md="1">
          <b-form-group
            label="Start Col"
            label-class="small"
          >
            <b-form-input
              v-model="form.start_column"
              size="sm"
              placeholder="A"
              maxlength="3"
              @input="form.start_column = uppercaseColumn(form.start_column)"
            />
          </b-form-group>
        </b-col>
        <b-col md="1">
          <b-form-group
            label="End Col"
            label-class="small"
          >
            <b-form-input
              v-model="form.end_column"
              size="sm"
              placeholder="H"
              maxlength="3"
              @input="form.end_column = uppercaseColumn(form.end_column)"
            />
          </b-form-group>
        </b-col>
        <b-col md="1">
          <b-form-group
            label="Date Col"
            label-class="small"
          >
            <b-form-input
              v-model="form.date_column"
              size="sm"
              placeholder="B"
              maxlength="3"
              @input="form.date_column = uppercaseColumn(form.date_column)"
            />
          </b-form-group>
        </b-col>
        <b-col md="1">
          <b-form-group
            label="Credit Col"
            label-class="small"
          >
            <b-form-input
              v-model="form.credit_column"
              size="sm"
              placeholder="C"
              maxlength="3"
              @input="form.credit_column = uppercaseColumn(form.credit_column)"
            />
          </b-form-group>
        </b-col>
        <b-col md="1">
          <b-form-group
            label="Debit Col"
            label-class="small"
          >
            <b-form-input
              v-model="form.debit_column"
              size="sm"
              placeholder="D"
              maxlength="3"
              @input="form.debit_column = uppercaseColumn(form.debit_column)"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="2"
          class="d-flex align-items-end pb-1"
        >
          <b-button
            variant="primary"
            size="sm"
            :disabled="saving || !isFormValid"
            @click="saveSettings"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-half"
            />
            {{ editingItem ? 'Update' : 'Save' }}
          </b-button>
          <b-button
            v-if="editingItem"
            variant="outline-secondary"
            size="sm"
            class="ml-half"
            @click="resetForm"
          >
            Cancel
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Filter -->
    <b-row class="mb-1">
      <b-col md="4">
        <b-form-group
          label="Filter by Supplier"
          class="mb-0"
        >
          <custom-v-select
            v-model="filterSupplier"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="supplierOptions"
            :loading="supplierOptionsLoading"
            label="text"
            placeholder="All Suppliers"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Loading -->
    <div
      v-if="loading"
      class="text-center my-3"
    >
      <b-spinner
        label="Loading"
        variant="primary"
      />
    </div>

    <!-- Error -->
    <b-alert
      variant="danger"
      :show="!!loadingError"
    >
      <div class="alert-body">
        {{ loadingError }}
      </div>
    </b-alert>

    <!-- Table -->
    <div
      v-if="!loading && !loadingError"
      class="table-scroll-wrapper"
    >
      <b-table
        :items="results"
        :fields="tableFields"
        responsive="sm"
        striped
        hover
        class="compact-table"
      >
        <template #cell(supplier_identifier)="{ item }">
          {{ item.supplier_identifier || item.supplier }}
        </template>
        <template #cell(action)="{ item }">
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="Edit Settings"
            icon="Edit2Icon"
            size="20"
            class="cursor-pointer"
            @click="startEdit(item)"
          />
        </template>
      </b-table>

      <p
        v-if="results.length === 0"
        class="text-center text-muted my-2"
      >
        No supplier ledger settings found.
      </p>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BTable, BRow, BCol, BButton, BFormGroup, BFormInput,
  BSpinner, BAlert, BCard, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BModal,
    BTable,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BSpinner,
    BAlert,
    BCard,
    CustomVSelect,
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      results: [],
      saving: false,

      // Supplier options
      supplierOptions: [],
      supplierOptionsLoading: false,

      // Filter
      filterSupplier: null,

      // Form
      form: {
        supplier: null,
        start_row: '',
        start_column: '',
        end_column: '',
        date_column: '',
        credit_column: '',
        debit_column: '',
      },
      editingItem: null,

      tableFields: [
        { key: 'supplier_identifier', label: 'Supplier' },
        { key: 'start_row', label: 'Start Row' },
        { key: 'start_column', label: 'Start Col' },
        { key: 'end_column', label: 'End Col' },
        { key: 'date_column', label: 'Date Col' },
        { key: 'credit_column', label: 'Credit Col' },
        { key: 'debit_column', label: 'Debit Col' },
        { key: 'action', label: 'Action' },
      ],
    }
  },
  computed: {
    isFormValid() {
      return this.form.supplier
        && this.form.start_row
        && this.form.start_column
        && this.form.end_column
        && this.form.date_column
        && this.form.credit_column
        && this.form.debit_column
    },
  },
  created() {
    this.fetchSupplierOptions()
    this.fetchSettings()
  },
  methods: {
    uppercaseColumn(value) {
      return value.toUpperCase().replace(/[^A-Z]/g, '')
    },

    async fetchSupplierOptions() {
      this.supplierOptionsLoading = true
      try {
        const res = await axios.get('/supplier_options')
        this.supplierOptions = res.data.data
      } catch {
        this.supplierOptions = []
      } finally {
        this.supplierOptionsLoading = false
      }
    },

    filtersChangeHandler() {
      this.fetchSettings()
    },

    fetchSettings() {
      this.loading = true
      const params = {}
      if (this.filterSupplier) {
        params.supplier = this.filterSupplier.id
      }

      axios.get('/supplier-ledger-settings/', { params })
        .then(res => {
          this.results = Array.isArray(res.data) ? res.data : (res.data.results || [])
          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.error
            || error?.response?.data?.detail
            || 'Something went wrong'
          this.loading = false
        })
    },

    startEdit(item) {
      this.editingItem = item
      this.form = {
        supplier: item.supplier,
        start_row: item.start_row,
        start_column: item.start_column || '',
        end_column: item.end_column || '',
        date_column: item.date_column || '',
        credit_column: item.credit_column || '',
        debit_column: item.debit_column || '',
      }
    },

    resetForm() {
      this.editingItem = null
      this.form = {
        supplier: null,
        start_row: '',
        start_column: '',
        end_column: '',
        date_column: '',
        credit_column: '',
        debit_column: '',
      }
    },

    async saveSettings() {
      if (this.saving) return
      this.saving = true

      const payload = {
        supplier: this.form.supplier,
        start_row: this.form.start_row,
        start_column: this.form.start_column,
        end_column: this.form.end_column,
        date_column: this.form.date_column,
        credit_column: this.form.credit_column,
        debit_column: this.form.debit_column,
      }

      try {
        if (this.editingItem) {
          await axios.patch(`/supplier-ledger-settings/${this.editingItem.id}/`, payload)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Settings Updated',
              text: 'Supplier ledger settings updated successfully',
              variant: 'success',
            },
          })
        } else {
          await axios.post('/supplier-ledger-settings/', payload)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Settings Created',
              text: 'Supplier ledger settings created successfully',
              variant: 'success',
            },
          })
        }
        this.resetForm()
        this.fetchSettings()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Save Failed',
            text: error?.response?.data?.error
              || error?.response?.data?.detail
              || 'Something went wrong',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ml-half {
  margin-left: 0.25rem;
}

.mr-half {
  margin-right: 0.25rem;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}
</style>
